import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { createCategory, updateCategory, getCategories, getCategoryById } from '../../services/category';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const CategoryModal = ({ open, mode, category, parentCategory, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [allCategories, setAllCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent: null,
    image_url: '',
    icon: '',
    is_active: true,
    is_featured: false,
    display_order: 0,
    compliance_policy: 'inherit',
    compliance_documents_text: '',
    compliance_notes: '',
    merchant_selection_mode: 'inherit',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    translations: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      initializeModal();
    }
  }, [open, mode, category, parentCategory]);

  const initializeModal = async () => {
    const categories = await loadCategories();

    if ((mode === 'edit' || mode === 'view') && category?.sid) {
      await loadCategoryDetails(category.sid, categories);
      return;
    }

    if (mode === 'create') {
      setFormData({
        name: '',
        slug: '',
        description: '',
        parent: parentCategory || null,
        image_url: '',
        icon: '',
        is_active: true,
        is_featured: false,
        display_order: 0,
        compliance_policy: 'inherit',
        compliance_documents_text: '',
        compliance_notes: '',
        merchant_selection_mode: 'inherit',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        translations: [],
      });
    }
  };

  const loadCategoryDetails = async (categoryId, availableCategories = []) => {
    try {
      setLoading(true);
      const response = await getCategoryById(categoryId);
      
      if (response) {
        // Handle different response structures
        const categoryData = response.data || response;
        
        // Find the parent category object from allCategories
        const parentCategory = categoryData.parent_sid ? 
          availableCategories.find(cat => cat.sid === categoryData.parent_sid) : null;
        
        setFormData({
          name: categoryData.name || '',
          slug: categoryData.slug || '',
          description: categoryData.description || '',
          parent: parentCategory,
          image_url: categoryData.image_url || '',
          icon: categoryData.icon || '',
          is_active: categoryData.is_active !== undefined ? categoryData.is_active : true,
          is_featured: categoryData.is_featured || false,
          display_order: categoryData.display_order || 0,
          compliance_policy: categoryData.compliance_policy || 'inherit',
          compliance_documents_text: Array.isArray(categoryData.compliance_documents)
            ? categoryData.compliance_documents.join('\n')
            : '',
          compliance_notes: categoryData.compliance_notes || '',
          merchant_selection_mode: categoryData.merchant_selection_mode || 'inherit',
          meta_title: categoryData.meta_title || '',
          meta_description: categoryData.meta_description || '',
          meta_keywords: categoryData.meta_keywords || '',
          translations: categoryData.translations || [],
        });
      } else {
        setErrors({ general: 'No category data received' });
      }
    } catch (error) {
      console.error('Error loading category details:', error);
      setErrors({ general: 'Failed to load category details' });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      if (response.success) {
        const categories = response.data || response.results || [];
        setAllCategories(categories);
        return categories;
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
    return [];
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      payload.compliance_documents = (payload.compliance_documents_text || '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      delete payload.compliance_documents_text;
      
      // Handle parent field - convert object to SID if needed
      if (payload.parent && typeof payload.parent === 'object') {
        payload.parent = payload.parent.sid;
      }
      
      // Remove empty fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null) {
          delete payload[key];
        }
      });

      if (mode === 'create') {
        await createCategory(payload);
      } else       if (mode === 'edit') {
        await updateCategory(category.sid, payload);
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving category:', error);
      
      // Extract specific error messages from backend response
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle different error response formats
        if (errorData.errors) {
          // Field-specific errors
          setErrors(errorData.errors);
        } else if (errorData.details) {
          // Backend validation errors with details
          const fieldErrors = {};
          Object.keys(errorData.details).forEach(field => {
            const fieldError = errorData.details[field];
            if (Array.isArray(fieldError)) {
              fieldErrors[field] = fieldError[0]; // Take first error message
            } else {
              fieldErrors[field] = fieldError;
            }
          });
          setErrors(fieldErrors);
        } else if (errorData.message) {
          // General error message
          setErrors({ general: errorData.message });
        } else if (errorData.error) {
          // Error field
          setErrors({ general: errorData.error });
        } else {
          // Fallback to generic message
          setErrors({ general: 'Failed to save category. Please try again.' });
        }
      } else if (error.message) {
        // Network or other errors
        setErrors({ general: error.message });
      } else {
        // Fallback to generic message
        setErrors({ general: 'Failed to save category. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const isViewMode = mode === 'view';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' && 'Add New Category'}
        {mode === 'edit' && 'Edit Category'}
        {mode === 'view' && 'View Category'}
      </DialogTitle>
      
      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        {loading && mode === 'edit' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
              <Tab label="Basic Info" />
              <Tab label="SEO" />
              <Tab label="Compliance" />
              <Tab label="Translations" disabled={mode === 'create'} />
            </Tabs>

        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
              disabled={isViewMode}
              fullWidth
            />

            <TextField
              label="Slug"
              value={formData.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              error={!!errors.slug}
              helperText={errors.slug || "Auto-generated if left empty"}
              disabled={isViewMode}
              fullWidth
            />

            <Autocomplete
              options={allCategories}
              getOptionLabel={(option) => option.name || ''}
              value={formData.parent || null}
              onChange={(e, newValue) => handleChange('parent', newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Parent Category" 
                  error={!!errors.parent}
                  helperText={errors.parent || "Leave empty for root category"}
                />
              )}
              disabled={isViewMode || (mode === 'create' && parentCategory)}
              fullWidth
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              error={!!errors.description}
              helperText={errors.description}
              disabled={isViewMode}
              multiline
              rows={3}
              fullWidth
            />

            <TextField
              label="Image URL"
              value={formData.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              disabled={isViewMode}
              fullWidth
            />

            <TextField
              label="Icon"
              value={formData.icon}
              onChange={(e) => handleChange('icon', e.target.value)}
              helperText="Enter emoji or icon identifier"
              disabled={isViewMode}
              fullWidth
            />

            <TextField
              label="Display Order"
              type="number"
              value={formData.display_order}
              onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
              disabled={isViewMode}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    disabled={isViewMode}
                  />
                }
                label="Active"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_featured}
                    onChange={(e) => handleChange('is_featured', e.target.checked)}
                    disabled={isViewMode}
                  />
                }
                label="Featured"
              />
            </Box>
          </Box>
        </TabPanel>

        {/* SEO Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Meta Title"
              value={formData.meta_title}
              onChange={(e) => handleChange('meta_title', e.target.value)}
              disabled={isViewMode}
              fullWidth
            />

            <TextField
              label="Meta Description"
              value={formData.meta_description}
              onChange={(e) => handleChange('meta_description', e.target.value)}
              disabled={isViewMode}
              multiline
              rows={3}
              fullWidth
            />

            <TextField
              label="Meta Keywords"
              value={formData.meta_keywords}
              onChange={(e) => handleChange('meta_keywords', e.target.value)}
              helperText="Comma-separated keywords"
              disabled={isViewMode}
              fullWidth
            />
          </Box>
        </TabPanel>

        {/* Compliance Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Configure how merchants may request this category and what verification is required. If no policy is set in this category or its parents, it is treated as open.
            </Alert>

            <FormControl fullWidth disabled={isViewMode}>
              <InputLabel>Verification Requirement</InputLabel>
              <Select
                label="Verification Requirement"
                value={formData.compliance_policy}
                onChange={(e) => handleChange('compliance_policy', e.target.value)}
              >
                <MenuItem value="inherit">Inherit from parent / Open if none</MenuItem>
                <MenuItem value="open">Open - no extra category documents</MenuItem>
                <MenuItem value="manual_review">Manual review</MenuItem>
                <MenuItem value="document_required">Document required</MenuItem>
                <MenuItem value="restricted">Restricted - admin only</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={isViewMode}>
              <InputLabel>Merchant Selection Rule</InputLabel>
              <Select
                label="Merchant Selection Rule"
                value={formData.merchant_selection_mode}
                onChange={(e) => handleChange('merchant_selection_mode', e.target.value)}
              >
                <MenuItem value="inherit">Inherit from parent / Allow if none</MenuItem>
                <MenuItem value="allow_parent">Allow merchants to select this category</MenuItem>
                <MenuItem value="require_leaf">Require final leaf subcategory</MenuItem>
                <MenuItem value="require_child_if_mixed_risk">Require child only if children have mixed verification rules</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Required Documents"
              value={formData.compliance_documents_text}
              onChange={(e) => handleChange('compliance_documents_text', e.target.value)}
              helperText="One document per line. Used when verification requirement is Document required, and shown to merchants during business verification."
              disabled={isViewMode}
              multiline
              rows={5}
              fullWidth
            />

            <TextField
              label="Internal Compliance Notes"
              value={formData.compliance_notes}
              onChange={(e) => handleChange('compliance_notes', e.target.value)}
              helperText="Internal admin guidance for reviewing merchants in this category."
              disabled={isViewMode}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </TabPanel>

        {/* Translations Tab */}
        <TabPanel value={tabValue} index={3}>
          <Alert severity="info">
            Translation management coming soon. For now, translations can be added via API.
          </Alert>
        </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {isViewMode ? 'Close' : 'Cancel'}
        </Button>
        {!isViewMode && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CategoryModal;
